import {
  CardModule,
  IconModule,
  SurveyFeedbackComponent,
  SurveyRatingComponent,
} from '@yoimo/joymo-ui';
import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';

import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { VideoWithDocId } from '@yoimo/client-sdk/videos';
import { EventWithDocId } from '@yoimo/client-sdk/events';
import { SaveFeedbackRequest } from '@yoimo/interfaces';
import {
  getAuthenticatedUserInfo,
  saveFeedback,
  shouldAskFeedback,
} from '@yoimo/client-sdk/users';
import { Functions } from '@angular/fire/functions';
import { Firestore } from '@angular/fire/firestore';
import { ChannelWithDocId } from '@yoimo/client-sdk/channels';
import { Auth } from '@angular/fire/auth';
import {
  catchError,
  map,
  merge,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';
import { UserAccessType } from '@core/services';

export type VideoPlayerTrigger = {
  trigger: 'PLAY' | 'PAUSE' | 'STOP';
  /** In milliseconds */
  time: number;
};
type VideoStopped = {
  type: 'STOPPED';
  /** In milliseconds */
  duration: number;
};
type VideoPlaying = {
  type: 'PLAYING';
  /** In milliseconds */
  startedAt: number;
};
type VideoPlayerStatus = VideoStopped | VideoPlaying;
export const MINUTE_IN_MILLISECONDS = 60000;

@Component({
  standalone: true,
  imports: [
    CommonModule,
    IconModule,
    CardModule,
    RouterModule,
    SurveyFeedbackComponent,
    SurveyRatingComponent,
  ],
  selector: 'joymo-video-feedback',
  templateUrl: './video-feedback.component.html',
  styleUrls: ['./video-feedback.component.scss'],
  host: {
    '[class.block]': 'showFeedbackPopup',
    '[class.none]': '!showFeedbackPopup',
    class: 'm-8 md:m-0',
  },
})
export class VideoFeedbackComponent implements OnChanges, OnDestroy {
  @Input() channel?: ChannelWithDocId;
  @Input() video?: VideoWithDocId;
  @Input() event?: EventWithDocId | null;
  @Input() userAccessType?: UserAccessType;
  @Input() videoPlayerTrigger$!: Observable<VideoPlayerTrigger>;
  @Input() isPlayerInFullScreen$!: Observable<boolean>;

  private _timer$ = new Subject<number>();
  private destroy$ = new Subject<void>();

  private timerRef?: ReturnType<typeof setInterval>;
  isPlayerInFullScreen = false;
  // If video is playing
  isPlaying = false;
  // Last video play/start time
  startTime?: number;
  // Last video pause/stop time
  stopTime?: number;

  /** Tracks user's cumulative watch time until now in milliseconds. */
  cumulativeTimeWatched = 0;
  /** Tracks user's total watch time until last pause/stop time in milliseconds. */
  totalTimeWatchedUntilLastPause = 0;

  userTestingGroup?: string;
  showFeedbackPopup = false;

  readonly timer$ = this._timer$.asObservable().pipe(
    map((time) => {
      return { type: 'CLOCK' as const, time };
    })
  );

  step: 'RATING' | 'FEEDBACK' = 'RATING';
  videoSurveyRatingCtx = {
    title: $localize`:@@videoSurveyRatingTitle: How was the stream?`,
    description: $localize`:@@videoSurveyRatingDescription: Please take into consideration the video quality, the camera placement and commentary if any.`,
    minLabel: $localize`:@@videoSurveyRatingMinLabel: Very poor`,
    maxLabel: $localize`:@@videoSurveyRatingMaxLabel: Excellent`,
  };
  videoSurveyFeedbackCtx = {
    constructiveTitle: $localize`:@@videoSurveyFeedbackConstructiveTitle: What could we improve?`,
    positiveTitle: $localize`:@@videoSurveyFeedbackPositiveTitle: What did we do well?`,
    submitLabel: $localize`:@@videoSurveyFeedbackSubmitLabel: Send feedback`,
  };
  protected ratingValue: number | null = null;
  protected rating: number | null = null;

  constructor(
    private fs: Firestore,
    private ff: Functions,
    private auth: Auth
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['video']) {
      // Re-initialize every time video changes.
      this.step = 'RATING';
      this.initializeTriggers();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    clearInterval(this.timerRef);
    this._timer$.next(0);
    this._timer$.complete();
  }

  initializeTriggers() {
    // Start a clock that triggers every minute
    this.timerRef = setInterval(() => {
      this._timer$.next(Date.now());
    }, MINUTE_IN_MILLISECONDS);

    getAuthenticatedUserInfo(this.auth, this.fs)
      ?.pipe(
        switchMap((userInfo) => {
          if (!this.channel) throw new Error('Channel is undefined');
          if (!this.video) throw new Error('Video is undefined');
          return shouldAskFeedback(
            this.fs,
            userInfo,
            this.channel.docId,
            this.video.docId
          );
        }),
        switchMap((response) => {
          if (!response.show || response.waitTime === undefined) {
            // If no feedback is needed call destroy to unsubscribe all listeners.
            this.ngOnDestroy();
            return of(false);
          }
          this.userTestingGroup = response.testingGroup;
          return this.showFeedback$(response.waitTime * MINUTE_IN_MILLISECONDS);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((result) => {
        this.showFeedbackPopup = result;
        // Unsubscribe from all listeners once the feedback form is shown
        if (this.showFeedbackPopup) this.ngOnDestroy();
      });
  }

  /**
   * Given the wait time it answers if user should be see feedback popup by
   * checking if user has video watched for a minimum wait time and is not in full screen
   * @param waitTime in milliseconds
   */
  showFeedback$(waitTime: number): Observable<boolean> {
    return merge(
      this.timer$,
      this.getPlayStatus$(),
      this.isPlayerInFullScreen$.pipe(
        map((val) => {
          this.isPlayerInFullScreen = val;
          return {
            type: 'FULL_SCREEN_CHANGE' as const,
            isPlayerInFullScreen: val,
          };
        })
      )
    ).pipe(
      map((val) => {
        if (val.type === 'CLOCK' && this.isPlaying && this.startTime) {
          // Every minute, if video is playing calculate the cumulativeTimeWatched
          this.cumulativeTimeWatched =
            this.totalTimeWatchedUntilLastPause + (val.time - this.startTime);
        }
        return (
          (this.totalTimeWatchedUntilLastPause >= waitTime ||
            this.cumulativeTimeWatched >= waitTime) &&
          !this.isPlayerInFullScreen
        );
      }),
      takeUntil(this.destroy$)
    );
  }

  /**
   * Returns status of the player.
   * If currently video is playing it returns the start time
   * If video is paused/stopped it returns the duration between the last play and pause/stop
   */
  getPlayStatus$(): Observable<VideoPlayerStatus> {
    let state: VideoPlayerStatus;

    return this.videoPlayerTrigger$.pipe(
      map((val) => {
        if (val.trigger === 'PLAY') {
          this.isPlaying = true;
          this.startTime = val.time;
          state = {
            type: 'PLAYING',
            startedAt: this.startTime,
          };
        }
        if (val.trigger === 'PAUSE' || val.trigger === 'STOP') {
          this.isPlaying = false;
          this.stopTime = val.time;
          if (!this.startTime) throw new Error('Start time is undefined');
          state = {
            type: 'STOPPED',
            duration: this.stopTime - this.startTime,
          };
          this.totalTimeWatchedUntilLastPause += state.duration;
        }

        return state;
      })
    );
  }

  submitRating(response: { rating: number; value: number }) {
    this.rating = response.rating;
    this.ratingValue = response.value;
    this.step = 'FEEDBACK';
  }

  saveStreamFeedback(ratingValue: number | null, message?: string) {
    this.showFeedbackPopup = false;

    if (!this.channel) throw new Error('Channel is undefined');
    if (!this.video) throw new Error('Video is undefined');
    if (!this.userAccessType) throw new Error('User access type is undefined');
    if (!this.userTestingGroup)
      throw new Error("user's testing group is undefined");

    const userFeedback: SaveFeedbackRequest = {
      type: 'VIDEO',
      feedback: {
        rating: ratingValue !== null ? ratingValue : null,
        message: message || null,
      },
      videoId: this.video.docId,
      videoType: this.video.type,
      isLive: this.video.streamState === 'LIVE',
      eventId: this.event ? this.event.docId : null,
      channelId: this.channel.docId,
      testingGroup: this.userTestingGroup,
      accessType: this.userAccessType,
    };

    saveFeedback(this.ff, userFeedback)
      .pipe(
        catchError((e) => {
          throw new Error(e);
        }),
        take(1)
      )
      .subscribe();
  }
}
